import { KeywordItem, MicroMarket, Typology } from '../types/seo';

export interface GscCredentials {
  client_email: string;
  private_key: string;
  project_id: string;
}

export interface GscQueryRow {
  keys: string[]; // [query, page]
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
}

export interface GscSyncConfig {
  siteUrl: string;
  clientEmail: string;
  credentialsJson?: string;
  lastSyncedAt?: string;
  dateRange: '7d' | '28d' | '90d';
  minImpressions: number;
}

// ─── Query Classification Logic for Surat & Vanakla Corridors ──────────────

export function classifyGscQuery(query: string): {
  microMarket: MicroMarket | 'All Surat';
  typology: Typology | 'General';
  intent: 'High Commercial' | 'Investigational' | 'Transactional' | 'Navigational';
  possessionTarget: 'Ready to Move' | 'Dec 2026 Handover' | 'Any';
  suggestedAdGroup: string;
  isNegative: boolean;
  exclusionReason?: string;
} {
  const q = query.toLowerCase();

  // Negative Check
  const negativeKeywords = ['rent', 'rental', 'pg hostel', 'room sharing', 'cheap lodge', 'furniture', 'party plot', 'dispute', 'demolition'];
  const matchedNegative = negativeKeywords.find((n) => q.includes(n));
  if (matchedNegative) {
    return {
      microMarket: 'All Surat',
      typology: 'General',
      intent: 'Investigational',
      possessionTarget: 'Any',
      suggestedAdGroup: 'Excluded Queries',
      isNegative: true,
      exclusionReason: `Contains negative term "${matchedNegative}"`,
    };
  }

  // Micro-Market Classification
  let microMarket: MicroMarket | 'All Surat' = 'All Surat';
  if (q.includes('vanakla')) {
    microMarket = 'Vanakla';
  } else if (q.includes('jahangirpura')) {
    microMarket = 'Jahangirpura';
  } else if (q.includes('palanpur') || q.includes('canal')) {
    microMarket = 'Palanpur Canal';
  } else if (q.includes('jahangirabad')) {
    microMarket = 'Jahangirabad';
  } else if (q.includes('dandi')) {
    microMarket = 'Dandi Road';
  } else if (q.includes('vesu')) {
    microMarket = 'Vesu';
  } else if (q.includes('pal')) {
    microMarket = 'Pal';
  } else if (q.includes('adajan')) {
    microMarket = 'Adajan';
  }

  // Typology Classification
  let typology: Typology | 'General' = 'General';
  if (q.includes('2 bhk') || q.includes('2bhk') || q.includes('2 bedroom')) {
    typology = '2 BHK Flat';
  } else if (q.includes('3 bhk') || q.includes('3bhk')) {
    typology = '3 BHK Luxury Apartment';
  } else if (q.includes('shop') || q.includes('commercial') || q.includes('retail')) {
    typology = 'Commercial Retail Shop';
  } else if (q.includes('office')) {
    typology = 'Executive Office Space';
  }

  // Possession Classification
  let possessionTarget: 'Ready to Move' | 'Dec 2026 Handover' | 'Any' = 'Any';
  if (q.includes('ready') || q.includes('immediate') || q.includes('lagom')) {
    possessionTarget = 'Ready to Move';
  } else if (q.includes('dec 2026') || q.includes('2026') || q.includes('avsar') || q.includes('under construction')) {
    possessionTarget = 'Dec 2026 Handover';
  }

  // Intent Classification
  let intent: 'High Commercial' | 'Investigational' | 'Transactional' | 'Navigational' = 'Investigational';
  if (q.includes('swarna') || q.includes('lagom') || q.includes('avsar')) {
    intent = 'Navigational';
  } else if (q.includes('price') || q.includes('cost') || q.includes('buy') || q.includes('booking')) {
    intent = 'Transactional';
  } else if (q.includes('ready') || q.includes('flats in') || q.includes('2 bhk') || q.includes('under construction')) {
    intent = 'High Commercial';
  }

  // Suggested Ad Group
  let suggestedAdGroup = 'Surat - General Real Estate';
  if (q.includes('lagom') || (microMarket === 'Vanakla' && possessionTarget === 'Ready to Move')) {
    suggestedAdGroup = 'Surat - Swarna Lagom Ready 2 BHK (Vanakla / Jahangirpura)';
  } else if (q.includes('avsar') || possessionTarget === 'Dec 2026 Handover') {
    suggestedAdGroup = 'Surat - Swarna Avsar Dec 2026 2 BHK (Vanakla / Palanpur Canal)';
  } else if (microMarket === 'Jahangirpura' || microMarket === 'Jahangirabad') {
    suggestedAdGroup = 'Jahangirabad & Palanpur - 2 BHK High Intent';
  } else if (typology === 'Commercial Retail Shop' || microMarket === 'Dandi Road') {
    suggestedAdGroup = 'Commercial Shops - Vanakla / Dandi Road Surat';
  }

  return {
    microMarket,
    typology,
    intent,
    possessionTarget,
    suggestedAdGroup,
    isNegative: false,
  };
}

// ─── Process Ingested GSC Queries into Keyword Pool ─────────────────────────

export function parseGscRowsToKeywords(
  rows: { query: string; impressions: number; clicks: number; ctr: number; position: number }[]
): KeywordItem[] {
  return rows.map((r, idx) => {
    const meta = classifyGscQuery(r.query);
    const ctrPct = parseFloat((r.ctr * 100).toFixed(2));
    const pos = parseFloat(r.position.toFixed(1));
    let performanceStatus: 'top_performer' | 'new_unranked' | 'low_ctr_alert' | 'underperforming' = 'new_unranked';
    if (meta.isNegative) {
      performanceStatus = 'underperforming';
    } else if (pos <= 3.0 && ctrPct >= 5.0) {
      performanceStatus = 'top_performer';
    } else if (r.impressions >= 1000 && ctrPct < 2.0) {
      performanceStatus = 'low_ctr_alert';
    } else if (pos > 10.0) {
      performanceStatus = 'underperforming';
    }

    return {
      id: `gsc-kw-${Date.now()}-${idx}`,
      query: r.query.trim().toLowerCase(),
      monthlySearches: 0,
      impressions: r.impressions,
      clicks: r.clicks,
      ctr: ctrPct,
      avgPosition: pos,
      performanceStatus,
      history: [],
      intent: meta.intent,
      microMarket: meta.microMarket,
      typology: meta.typology,
      budgetTier: undefined,
      possessionTarget: meta.possessionTarget,
      isExcluded: meta.isNegative,
      exclusionReason: meta.exclusionReason,
      suggestedAdGroup: meta.suggestedAdGroup,
    };
  });
}
